import { readStringClaim } from "@/lib/auth/claims";

const NAME_KEYS = ["full_name", "name", "display_name"] as const;

function readTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readRecordString(record: unknown, key: string): string | null {
  if (typeof record !== "object" || record === null) return null;
  return readTrimmedString((record as Record<string, unknown>)[key]);
}

function readNameFromRecord(record: unknown): string | null {
  if (typeof record !== "object" || record === null) return null;

  for (const key of NAME_KEYS) {
    const value = readRecordString(record, key);
    if (value) return value;
  }

  const given = readRecordString(record, "given_name") ?? readRecordString(record, "givenName");
  const family = readRecordString(record, "family_name") ?? readRecordString(record, "familyName");
  const composed = [given, family].filter(Boolean).join(" ");
  return composed.length > 0 ? composed : null;
}

function readMetadataRecord(record: unknown, key: string): Record<string, unknown> | null {
  if (typeof record !== "object" || record === null) return null;
  const value = (record as Record<string, unknown>)[key];
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readNameFromIdentities(user: AuthUserDisplaySnapshot | null | undefined): string | null {
  for (const identity of user?.identities ?? []) {
    const name = readNameFromRecord(identity?.identity_data);
    if (name) return name;
  }
  return null;
}

export type AuthUserDisplaySnapshot = {
  email?: string | null;
  user_metadata?: Record<string, unknown> | null;
  identities?: Array<{
    identity_data?: Record<string, unknown> | null;
  } | null> | null;
};

/** Display label for an authenticated user: name, then email. */
export function resolveAuthUserDisplayLabel(
  user: AuthUserDisplaySnapshot | null | undefined
): string | null {
  const name = readNameFromRecord(user?.user_metadata) ?? readNameFromIdentities(user);
  const email = readTrimmedString(user?.email) ?? readRecordString(user?.user_metadata, "email");
  return name ?? email;
}

/** Display label from JWT claims when the Supabase user object is unavailable. */
export function resolveAuthUserDisplayLabelFromClaims(claims: unknown): string | null {
  const claimsMetadata = readMetadataRecord(claims, "user_metadata");
  const email =
    readStringClaim(claims, "email") ?? readRecordString(claimsMetadata, "email") ?? null;
  const name =
    readNameFromRecord(claims) ??
    readNameFromRecord(claimsMetadata) ??
    readDisplayNameFromUserMetadata(claims as Record<string, unknown>);
  return name ?? email;
}

export function readDisplayNameFromUserMetadata(
  metadata: Record<string, unknown> | null | undefined
): string | null {
  return readNameFromRecord(metadata);
}

export function readDisplayNameFromIdentities(
  identities: AuthUserDisplaySnapshot["identities"]
): string | null {
  return readNameFromIdentities({ identities });
}
